import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownAZ,
  ArrowUpAZ,
  Cake,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightIcon,
  Download,
  FileText,
  Filter,
  MessageCircle,
  Phone,
  Plus,
  Scale,
  Search,
  Users,
  X,
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  apiClient,
  type CaseStatus,
  type ClientCaseSummary,
  type ClientListItem,
  type ClientStatus,
  type ClientsListResponse,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { GlassSelect } from '@/components/ui/glass-select';
import { SelecionarModeloModal } from '@/components/documents/selecionar-modelo-modal';
import { TaskFormModal } from '@/components/tasks/task-form-modal';
import { useSession } from '@/lib/use-session';

const STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Lead',
  consultation_paid: 'Consulta paga',
  active: 'Ativo',
  inactive: 'Inativo',
};

const SOURCES = [
  'google',
  'instagram',
  'facebook',
  'indicacao',
  'promad',
  'whatsapp',
  'site',
  'outras',
];

const SORTS: Array<{
  key: string;
  label: string;
  sortBy: 'name' | 'createdAt';
  sortDir: 'asc' | 'desc';
}> = [
  { key: 'recent', label: 'Mais recentes', sortBy: 'createdAt', sortDir: 'desc' },
  { key: 'oldest', label: 'Mais antigos', sortBy: 'createdAt', sortDir: 'asc' },
  { key: 'name_asc', label: 'Nome (A→Z)', sortBy: 'name', sortDir: 'asc' },
  { key: 'name_desc', label: 'Nome (Z→A)', sortBy: 'name', sortDir: 'desc' },
];

const PAGE_SIZE = 20;

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '--';
  }
}

function normalizePrimary(
  pc: { type: string; value: string } | null | undefined,
): { type: 'whatsapp' | 'phone' | 'email' | null; value: string } {
  if (!pc) return { type: null, value: '' };
  if (pc.type === 'whatsapp' || pc.type === 'phone' || pc.type === 'email') {
    return { type: pc.type, value: pc.value };
  }
  return { type: null, value: '' };
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function whatsappLink(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${withCountry}`;
}

function FilterDropdown({
  status,
  source,
  onChange,
}: {
  status: ClientStatus | '';
  source: string;
  onChange: (next: { status: ClientStatus | ''; source: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const activeCount = (status ? 1 : 0) + (source ? 1 : 0);

  return (
    <div ref={ref} data-tour="clientes-filtrar" className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
          'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        Filtrar
        {activeCount > 0 && (
          <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold text-[var(--accent-fg)]">
            {activeCount}
          </span>
        )}
      </button>
      {open && (
        <div
          className={cn(
            'absolute right-0 z-20 mt-1 w-64 rounded-md border shadow-lg',
            'popover-menu',
          )}
        >
          <div className="flex flex-col gap-3 p-3 text-xs">
            <div>
              <label className="mb-1 block font-medium text-[var(--fg)]">
                Status
              </label>
              <GlassSelect
                value={status}
                onChange={(v) =>
                  onChange({ status: v as ClientStatus | '', source })
                }
                options={[
                  { value: '', label: 'Todos' },
                  ...(Object.keys(STATUS_LABELS) as ClientStatus[]).map((s) => ({
                    value: s,
                    label: STATUS_LABELS[s],
                  })),
                ]}
                className="w-full"
                aria-label="Status"
              />
            </div>
            <div>
              <label className="mb-1 block font-medium text-[var(--fg)]">
                Origem
              </label>
              <GlassSelect
                value={source}
                onChange={(v) => onChange({ status, source: v })}
                options={[
                  { value: '', label: 'Todas' },
                  ...SOURCES.map((s) => ({
                    value: s,
                    label: s.charAt(0).toUpperCase() + s.slice(1),
                  })),
                ]}
                className="w-full"
                aria-label="Origem"
              />
            </div>
            {activeCount > 0 && (
              <button
                type="button"
                onClick={() => onChange({ status: '', source: '' })}
                className="self-start text-[11px] font-medium text-[var(--accent)]"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SortDropdown({
  current,
  onChange,
}: {
  current: string;
  onChange: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const currentSort = SORTS.find((s) => s.key === current) ?? SORTS[0]!;
  const Icon = currentSort.sortDir === 'asc' ? ArrowUpAZ : ArrowDownAZ;

  return (
    <div ref={ref} data-tour="clientes-ordenar" className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
          'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
        Ordenar
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div
          className={cn(
            'absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-md border shadow-lg',
            'popover-menu',
          )}
        >
          <ul className="py-1">
            {SORTS.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(s.key);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-xs',
                    'text-[var(--fg)] hover:bg-[var(--surface-muted)]',
                    s.key === current && 'bg-[var(--surface-muted)] font-medium',
                  )}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ContactCell({
  primary,
}: {
  primary: { type: string; value: string } | null | undefined;
}) {
  const { type, value } = normalizePrimary(primary);
  if (!type) return <span className="text-[var(--fg-muted)]">N/A</span>;

  if (type === 'whatsapp' || type === 'phone') {
    const text = formatPhone(value);
    if (type === 'whatsapp') {
      return (
        <a
          href={whatsappLink(value)}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 text-emerald-500 hover:underline"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {text}
        </a>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-[var(--fg)]">
        <Phone className="h-3.5 w-3.5 text-[var(--fg-muted)]" />
        {text}
      </span>
    );
  }
  // email
  return <span className="text-[var(--fg)]">{value}</span>;
}

function TableSkeleton() {
  return (
    <tbody>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="border-b border-[var(--border)]">
          {Array.from({ length: 5 }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--surface-muted)]" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

const CASE_STATUS_LABEL: Record<CaseStatus, { label: string; cls: string }> = {
  active: {
    label: 'Ativo',
    cls: 'border-emerald-500/40 text-emerald-600 bg-emerald-500/10',
  },
  suspended: {
    label: 'Suspenso',
    cls: 'border-amber-500/40 text-amber-600 bg-amber-500/10',
  },
  archived: {
    label: 'Arquivado',
    cls: 'border-[var(--border)] text-[var(--fg-muted)] bg-[var(--surface-muted)]',
  },
  closed: {
    label: 'Encerrado',
    cls: 'border-[var(--border)] text-[var(--fg-muted)] bg-[var(--surface-muted)]',
  },
};

function CaseStatusBadge({ status }: { status: CaseStatus }) {
  const meta = CASE_STATUS_LABEL[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
        meta.cls,
      )}
    >
      {meta.label}
    </span>
  );
}

function formatCnj(cnj: string | null): string {
  if (!cnj) return '--';
  const d = cnj.replace(/\D/g, '');
  if (d.length !== 20) return cnj;
  return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13, 14)}.${d.slice(14, 16)}.${d.slice(16)}`;
}

function ClientCasesSubTable({
  clientId,
  onGenerateDocument,
  onAssignTask,
}: {
  clientId: string;
  onGenerateDocument: () => void;
  onAssignTask: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', 'cases', clientId],
    queryFn: () => apiClient.clientCases(clientId),
    staleTime: 1000 * 30,
  });

  if (isLoading) {
    return (
      <div className="px-4 py-3 text-xs text-[var(--fg-muted)]">
        Carregando processos...
      </div>
    );
  }
  if (isError) {
    return (
      <div className="px-4 py-3 text-xs text-red-500">
        Falha ao carregar processos.
      </div>
    );
  }
  const items: ClientCaseSummary[] = data ?? [];

  return (
    <div className="bg-[var(--surface-muted)]/30 px-4 py-3">
      {items.length === 0 ? (
        <div className="flex items-center gap-2 py-2 text-xs text-[var(--fg-muted)]">
          <Scale className="h-3.5 w-3.5" />
          Este cliente ainda não tem processos cadastrados.
        </div>
      ) : (
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
              <th className="px-2 py-1.5">Tipo de ação</th>
              <th className="px-2 py-1.5">Pessoas envolvidas</th>
              <th className="px-2 py-1.5">Número do processo</th>
              <th className="px-2 py-1.5">Data</th>
              <th className="px-2 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/escritorio/processos/${c.id}`)}
                className="cursor-pointer border-t border-[var(--border)]/40 hover:bg-[var(--surface)]/60"
              >
                <td className="px-2 py-1.5 text-[var(--fg)]">
                  {c.actionTypeName ?? '--'}
                  {c.currentStageName && (
                    <span className="ml-1 text-[10px] text-[var(--fg-muted)]">
                      · {c.currentStageName}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-[var(--fg-muted)]">
                  {c.mainPartyNames || '--'}
                </td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-[var(--fg)]">
                  {formatCnj(c.numeroCnj)}
                </td>
                <td className="px-2 py-1.5 text-[var(--fg-muted)]">
                  {c.openedAt
                    ? new Date(c.openedAt + 'T00:00:00').toLocaleDateString(
                        'pt-BR',
                      )
                    : '--'}
                </td>
                <td className="px-2 py-1.5">
                  <CaseStatusBadge status={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onAssignTask}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition',
            'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
          )}
        >
          <Plus className="h-3 w-3" />
          Atribuir tarefa
        </button>
        <button
          type="button"
          onClick={onGenerateDocument}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition',
            'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
          )}
        >
          <FileText className="h-3 w-3" />
          Gerar documento
        </button>
        <Link
          to={`/escritorio/processos/novo?clientId=${clientId}`}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition',
            'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
          )}
        >
          <Plus className="h-3 w-3" />
          Novo processo
        </Link>
      </div>
    </div>
  );
}

function EmptyRow() {
  return (
    <tbody>
      <tr>
        <td colSpan={5} className="px-4 py-16 text-center">
          <div className="flex flex-col items-center gap-3">
            <Users
              className="h-10 w-10 text-[var(--fg-muted)] opacity-40"
              strokeWidth={1.5}
            />
            <p className="max-w-sm text-sm text-[var(--fg-muted)]">
              Ainda não há contatos cadastrados. Comece adicionando o primeiro.
            </p>
          </div>
        </td>
      </tr>
    </tbody>
  );
}

export function ClientsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, tenantId } = useSession();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ClientStatus | ''>('');
  const [source, setSource] = useState('');
  const [sortKey, setSortKey] = useState<string>('recent');

  // Filtro de aniversariantes controlado pela URL (?aniversariantes=mes)
  const birthdaysFilter = searchParams.get('aniversariantes') === 'mes';
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [gerarModalState, setGerarModalState] = useState<{
    clientId: string;
    clientName: string;
  } | null>(null);
  const [tarefaClientId, setTarefaClientId] = useState<string | null>(null);

  const { data: taskCategories = [] } = useQuery({
    queryKey: ['task-categories'],
    queryFn: () => apiClient.taskCategoriesList({ active: 'true' }),
    staleTime: 5 * 60_000,
  });

  function toggleExpand(clientId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }

  // debounce simples do search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Resetar pagina quando filtro de aniversariantes muda
  useEffect(() => {
    setPage(1);
  }, [birthdaysFilter]);

  const sort = useMemo(
    () => SORTS.find((s) => s.key === sortKey) ?? SORTS[0]!,
    [sortKey],
  );

  const queryParams = {
    search: search || undefined,
    status: (status || undefined) as ClientStatus | undefined,
    source: source || undefined,
    page,
    pageSize: PAGE_SIZE,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir,
    birthdaysThisMonth: birthdaysFilter || undefined,
  };

  const { data, isLoading, isError, isFetching } =
    useQuery<ClientsListResponse>({
      queryKey: ['clients', 'list', queryParams],
      queryFn: () => apiClient.clientsList(queryParams),
      placeholderData: (prev) => prev,
    });

  function handleNewContact() {
    navigate('/escritorio/clientes/novo');
  }

  function handleExport() {
    window.alert('Exportação chega em breve.');
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-tour="clientes-novo-contato"
            onClick={handleNewContact}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition',
              'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            Novo contato
          </button>
          <div data-tour="clientes-busca" className="flex items-center">
          {showSearch ? (
            <div className="flex items-center gap-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--fg-muted)]" />
                <input
                  autoFocus
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Buscar por nome ou CPF/CNPJ..."
                  className="h-8 w-64 rounded-md border border-[var(--border)] bg-[var(--surface)] pl-7 pr-2 text-xs text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setShowSearch(false);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--fg-muted)] hover:bg-[var(--surface-muted)]"
                aria-label="Fechar busca"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
              )}
            >
              <Search className="h-3.5 w-3.5" />
              Buscar
            </button>
          )}
          </div>
          <FilterDropdown
            status={status}
            source={source}
            onChange={({ status: s, source: src }) => {
              setStatus(s);
              setSource(src);
              setPage(1);
            }}
          />
          {birthdaysFilter && (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium',
                'border-[var(--accent)]/40 bg-[var(--accent)]/10 text-[var(--accent)]',
              )}
            >
              <Cake className="h-3 w-3" />
              Aniversariantes do mês
              <button
                type="button"
                aria-label="Remover filtro de aniversariantes"
                onClick={() => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('aniversariantes');
                    return next;
                  });
                }}
                className="ml-0.5 rounded-full p-0.5 hover:bg-[var(--accent)]/20"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          )}
          <SortDropdown current={sortKey} onChange={setSortKey} />
          <button
            type="button"
            onClick={handleExport}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
            )}
          >
            <Download className="h-3.5 w-3.5" />
            Exportar
          </button>
        </div>
        <div className="text-xs text-[var(--fg-muted)]">
          {isFetching ? 'Atualizando...' : `${total} contato${total === 1 ? '' : 's'}`}
        </div>
      </div>

      <div data-tour="clientes-tabela" className="overflow-hidden rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--shadow-card)]">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--surface-muted)]/40 text-[10px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
                <th className="w-8 px-2 py-2.5"></th>
                <th className="px-4 py-2.5">Nome do cliente</th>
                <th className="px-4 py-2.5">Contato</th>
                <th className="px-4 py-2.5">Cidade/UF</th>
                <th className="px-4 py-2.5">Data do cadastro</th>
              </tr>
            </thead>
            {isLoading ? (
              <TableSkeleton />
            ) : isError ? (
              <tbody>
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-[var(--fg-muted)]">
                    Falha ao carregar contatos. Tente novamente.
                  </td>
                </tr>
              </tbody>
            ) : items.length === 0 ? (
              <EmptyRow />
            ) : (
              <tbody>
                {items.map((client: ClientListItem) => {
                  const isOpen = expanded.has(client.id);
                  return (
                    <Fragment key={client.id}>
                      <tr
                        className="border-b border-[var(--border)] transition hover:bg-[var(--surface-muted)]/30"
                      >
                        <td className="px-2 py-3">
                          <button
                            type="button"
                            onClick={() => toggleExpand(client.id)}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--fg-muted)] hover:bg-[var(--surface-muted)] hover:text-[var(--fg)]"
                            aria-label={isOpen ? 'Recolher' : 'Expandir processos'}
                            aria-expanded={isOpen}
                          >
                            <ChevronRightIcon
                              className={cn(
                                'h-3.5 w-3.5 transition-transform',
                                isOpen && 'rotate-90',
                              )}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/escritorio/clientes/${client.id}`}
                            className="font-medium text-[var(--fg)] hover:text-[var(--accent)] hover:underline"
                          >
                            {client.name}
                          </Link>
                          {client.tradeName && (
                            <p className="text-[11px] text-[var(--fg-muted)]">
                              {client.tradeName}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ContactCell primary={client.primaryContact} />
                        </td>
                        <td className="px-4 py-3 text-[var(--fg-muted)]">
                          {client.city && client.state
                            ? `${client.city}/${client.state}`
                            : client.city ?? client.state ?? '--'}
                        </td>
                        <td className="px-4 py-3 text-[var(--fg-muted)]">
                          {formatDate(client.createdAt)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-[var(--border)]">
                          <td colSpan={5} className="p-0">
                            <ClientCasesSubTable
                              clientId={client.id}
                              onGenerateDocument={() =>
                                setGerarModalState({
                                  clientId: client.id,
                                  clientName: client.name,
                                })
                              }
                              onAssignTask={() => setTarefaClientId(client.id)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {gerarModalState && (
          <SelecionarModeloModal
            clientId={gerarModalState.clientId}
            clientName={gerarModalState.clientName}
            open={true}
            onClose={() => setGerarModalState(null)}
          />
        )}
        {tarefaClientId && user?.id && (
          <TaskFormModal
            categories={taskCategories}
            onClose={() => setTarefaClientId(null)}
            onCreated={() => setTarefaClientId(null)}
            currentUserId={user.id}
            tenantId={tenantId ?? ''}
            clientId={tarefaClientId}
          />
        )}
        {items.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-[var(--border)] px-4 py-2.5 text-xs text-[var(--fg-muted)]">
            <span>
              Página {page} de {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={cn(
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border transition',
                  'border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]',
                  page <= 1 && 'cursor-not-allowed opacity-40',
                )}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={cn(
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border transition',
                  'border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]',
                  page >= totalPages && 'cursor-not-allowed opacity-40',
                )}
                aria-label="Próxima página"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
